import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationUtilityService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private sortedCommonKeys: string[] = [];
  private cachedTranslationMap: Map<string, string> = new Map();

  constructor(
    private translateService: TranslateService,
    private logger: LoggerService
  ) {
    this.updateTranslationCache();
    this.translateService.onLangChange.subscribe(() => {
      this.updateTranslationCache();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  /**
   * Returns the sorted keys for translation matching.
   */
  getSortedCommonKeys(): string[] {
    if (this.sortedCommonKeys.length === 0) {
      this.updateTranslationCache();
    }
    return this.sortedCommonKeys;
  }

  /**
   * Returns the map of cached common translations.
   */
  getCachedTranslationMap(): Map<string, string> {
    return this.cachedTranslationMap;
  }

  /**
   * Refreshes the local translation cache from the TranslateService.
   */
  public updateTranslationCache() {
    try {
      const commonTranslations = this.translateService.instant('COMMON');
      if (commonTranslations && typeof commonTranslations === 'object') {
        this.sortedCommonKeys = Object.keys(commonTranslations).sort((a, b) => b.length - a.length);
        this.cachedTranslationMap.clear();
        this.sortedCommonKeys.forEach(key => {
          const translated = this.getTranslatedText('COMMON.' + key);
          if (translated) {
            this.cachedTranslationMap.set(key, translated);
          }
        });
      }
    } catch (e) {
      this.logger.error('Failed to update notification translation cache', e);
    }
  }

  getTranslatedText(key: string): string { 
    const translation = this.translateService.instant(key); 
    return translation !== key ? translation : ''; 
  }

  /**
   * Translates relative time strings with correct grammar and word order (e.g. "Hace 2 semanas", "Il y a 2 semaines", "2 weeks ago").
   */
  translateTimeago(text: string): string {
    if (!text || typeof text !== 'string') return '';
    const trimmed = text.trim();
    if (!trimmed) return '';

    const currentLang = (this.translateService.currentLang || this.translateService.defaultLang || 'en').toLowerCase();
    const isEs = currentLang.startsWith('es');
    const isFr = currentLang.startsWith('fr');

    if (/^(just now|a few seconds ago)$/i.test(trimmed)) {
      if (isEs) return 'Justo ahora';
      if (isFr) return "À l'instant";
      return 'Just now';
    }

    const englishMatch = trimmed.match(/^(\d+|a|an)\s+([a-zA-Z]+)\s+ago$/i);
    const spanishMatch = trimmed.match(/^(\d+|un|una)\s+([a-zA-ZáéíóúÁÉÍÓÚñÑ]+)\s+hace$/i) || trimmed.match(/^hace\s+(\d+|un|una)\s+([a-zA-ZáéíóúÁÉÍÓÚñÑ]+)$/i);
    const frenchMatch = trimmed.match(/^(\d+|un|une)\s+([a-zA-ZàâäéèêëîïôöùûüçÀÂÄÉÈÊËÎÏÔÖÙÛÜÇ]+)\s+il\s+y\s+a$/i) || trimmed.match(/^il\s+y\s+a\s+(\d+|un|une)\s+([a-zA-ZàâäéèêëîïôöùûüçÀÂÄÉÈÊËÎÏÔÖÙÛÜÇ]+)$/i);

    let count = 1;
    let unitKey = '';

    const normalizeUnit = (u: string): string => {
      const lower = u.toLowerCase();
      if (/^second/.test(lower) || /^segund/.test(lower)) return 'second';
      if (/^minute/.test(lower) || /^minut/.test(lower)) return 'minute';
      if (/^hour/.test(lower) || /^hor/.test(lower) || /^heur/.test(lower)) return 'hour';
      if (/^day/.test(lower) || /^d[ií]a/.test(lower) || /^jour/.test(lower)) return 'day';
      if (/^week/.test(lower) || /^seman/.test(lower)) return 'week';
      if (/^month/.test(lower) || /^mes/.test(lower) || /^moi/.test(lower)) return 'month';
      if (/^year/.test(lower) || /^a[ñn]/.test(lower) || /^an/.test(lower)) return 'year';
      return '';
    };

    if (englishMatch) {
      count = (englishMatch[1].toLowerCase() === 'a' || englishMatch[1].toLowerCase() === 'an') ? 1 : parseInt(englishMatch[1], 10) || 1;
      unitKey = normalizeUnit(englishMatch[2]);
    } else if (spanishMatch) {
      count = (spanishMatch[1].toLowerCase() === 'un' || spanishMatch[1].toLowerCase() === 'una') ? 1 : parseInt(spanishMatch[1], 10) || 1;
      unitKey = normalizeUnit(spanishMatch[2]);
    } else if (frenchMatch) {
      count = (frenchMatch[1].toLowerCase() === 'un' || frenchMatch[1].toLowerCase() === 'une') ? 1 : parseInt(frenchMatch[1], 10) || 1;
      unitKey = normalizeUnit(frenchMatch[2]);
    }

    if (unitKey) {
      if (isEs) {
        const spanishUnits: Record<string, { singular: string; plural: string }> = {
          second: { singular: 'segundo', plural: 'segundos' },
          minute: { singular: 'minuto', plural: 'minutos' },
          hour: { singular: 'hora', plural: 'horas' },
          day: { singular: 'día', plural: 'días' },
          week: { singular: 'semana', plural: 'semanas' },
          month: { singular: 'mes', plural: 'meses' },
          year: { singular: 'año', plural: 'años' }
        };
        const u = spanishUnits[unitKey];
        const unitStr = count === 1 ? u.singular : u.plural;
        return `Hace ${count} ${unitStr}`;
      }

      if (isFr) {
        const frenchUnits: Record<string, { singular: string; plural: string }> = {
          second: { singular: 'seconde', plural: 'secondes' },
          minute: { singular: 'minute', plural: 'minutes' },
          hour: { singular: 'heure', plural: 'heures' },
          day: { singular: 'jour', plural: 'jours' },
          week: { singular: 'semaine', plural: 'semaines' },
          month: { singular: 'mois', plural: 'mois' },
          year: { singular: 'an', plural: 'ans' }
        };
        const u = frenchUnits[unitKey];
        const unitStr = count === 1 ? u.singular : u.plural;
        return `Il y a ${count} ${unitStr}`;
      }

      const englishUnits: Record<string, { singular: string; plural: string }> = {
        second: { singular: 'second', plural: 'seconds' },
        minute: { singular: 'minute', plural: 'minutes' },
        hour: { singular: 'hour', plural: 'hours' },
        day: { singular: 'day', plural: 'days' },
        week: { singular: 'week', plural: 'weeks' },
        month: { singular: 'month', plural: 'months' },
        year: { singular: 'year', plural: 'years' }
      };
      const u = englishUnits[unitKey];
      const unitStr = count === 1 ? u.singular : u.plural;
      return `${count} ${unitStr} ago`;
    }

    return trimmed;
  }
}
